// ============================================================
// Предзагрузка и кэш данных экранов.
// LoadingView дёргает все ручки заранее → экраны сразу рисуют кэш,
// а свои запросы делают как тихое обновление.
// load(key) — дедуп: если запрос уже идёт (например, из LoadingView), вернёт тот же промис.
// ============================================================
import {
  getCoinsBalance, getSpinPrizes, getMyPrizes, getCoinsHistory, dailyCheckin,
  moscowDay, setCachedCoins, CHECKIN_DAY_KEY
} from '@/services/coinsService';

const API = 'https://baaack.zabanishumresh.com';
const PREFIX = 'pf:';
export const CHECKIN_TOAST_KEY = 'checkin_toast';

const authHeader = () => {
  const t = sessionStorage.getItem('jwtToken') || '';
  return t.startsWith('Bearer ') ? t : `Bearer ${t}`;
};

const getJson = async (path, timeout = 10000) => {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeout);
  try {
    const res = await fetch(API + path, {
      headers: { accept: 'application/json', Authorization: authHeader() },
      signal: ctrl.signal
    });
    if (!res.ok) { const e = new Error(`HTTP ${res.status}`); e.status = res.status; throw e; }
    return await res.json();
  } finally { clearTimeout(timer); }
};

const FETCHERS = {
  devices:        () => getJson('/hwid/devices'),
  coinsBalance:   () => getCoinsBalance(),
  spinPrizes:     () => getSpinPrizes(),
  myPrizes:       () => getMyPrizes(true),          // все призы; купоны = active + discount
  coinsHistory:   () => getCoinsHistory(100, 0),
  refFriends:     () => getJson('/referral/friends'),
  refRewards:     () => getJson('/referral/rewards'),
  refLeaderboard: () => getJson('/referral/leaderboard'),
  refLinks:       () => getJson('/referral/links'),
};

// ===== Кэш: память + sessionStorage =====
const mem = new Map();       // key → { data, ts }
const inflight = new Map();  // key → Promise

const readEntry = (key) => {
  if (mem.has(key)) return mem.get(key);
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    mem.set(key, entry);
    return entry;
  } catch (e) { return null; }
};

export const peek = (key) => readEntry(key)?.data ?? null;
export const cacheAge = (key) => { const e = readEntry(key); return e ? Date.now() - e.ts : Infinity; };

export const setCache = (key, data) => {
  const entry = { data, ts: Date.now() };
  mem.set(key, entry);
  try { sessionStorage.setItem(PREFIX + key, JSON.stringify(entry)); } catch (e) { /* noop */ }
};

export const invalidate = (key) => {
  const e = readEntry(key);
  if (e) { e.ts = 0; try { sessionStorage.setItem(PREFIX + key, JSON.stringify(e)); } catch (err) { /* noop */ } }
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const isAuthError = (e) => e?.status === 401 || e?.status === 403;

async function withRetry(fn, retries) {
  let lastErr;
  for (let i = 0; i <= retries; i++) {
    try { return await fn(); } catch (e) {
      lastErr = e;
      if (isAuthError(e) || i === retries) break;
      await wait(800 * 2 ** i);
    }
  }
  throw lastErr;
}

/**
 * @param {string} key
 * @param {{ maxAge?: number, retries?: number }} opts
 *   maxAge — если кэш моложе, вернёт его без запроса (по умолчанию 0 = всегда обновлять)
 */
export function load(key, { maxAge = 0, retries = 1 } = {}) {
  if (maxAge > 0 && cacheAge(key) < maxAge) return Promise.resolve(peek(key));
  if (inflight.has(key)) return inflight.get(key);
  const fetcher = FETCHERS[key];
  if (!fetcher) return Promise.reject(new Error(`Unknown prefetch key: ${key}`));
  const p = withRetry(fetcher, retries)
    .then((data) => { setCache(key, data); return data; })
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

// Ежедневный чекин — один раз в день по Москве. Тост показывает Home.
export async function runCheckinOnce() {
  const today = moscowDay();
  try { if (sessionStorage.getItem(CHECKIN_DAY_KEY) === today) return null; } catch (e) { /* noop */ }
  const r = await withRetry(() => dailyCheckin(), 2);
  sessionStorage.setItem(CHECKIN_DAY_KEY, today);
  if (typeof r?.coins_total === 'number') setCachedCoins(r.coins_total);
  if (r && !r.already_done && r.coins_earned > 0) {
    sessionStorage.setItem(CHECKIN_TOAST_KEY, JSON.stringify(r));
  }
  return r;
}

/** Все второстепенные данные параллельно. Никогда не отклоняется. */
export function prefetchAll() {
  const coins = runCheckinOnce()
    .catch((e) => console.error('prefetch checkin:', e?.message || e))
    .then(() => load('coinsBalance', { retries: 2 }));
  const rest = Object.keys(FETCHERS)
    .filter((k) => k !== 'coinsBalance')
    .map((k) => load(k, { retries: 2 }));
  return Promise.allSettled([coins, ...rest]).then((res) => {
    res.forEach((r, i) => { if (r.status === 'rejected') console.error('prefetch failed:', i, r.reason?.message || r.reason); });
    return res;
  });
}
